import { getToken, onMessage, type Messaging } from 'firebase/messaging';
import { getFirebaseMessaging, firebaseConfig } from '@/lib/firebase';
import { apiClient } from '@/api/client';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined;
const SW_PATH = '/firebase-messaging-sw.js';
const FCM_TOKEN_KEY = 'fcm_token';

async function getOrRegisterServiceWorker(): Promise<ServiceWorkerRegistration> {
  // Pass the config via query string so the SW can initialise Firebase without
  // hardcoding any values in the worker file.
  const configParam = encodeURIComponent(JSON.stringify(firebaseConfig));
  const swUrl = `${SW_PATH}?firebaseConfig=${configParam}`;

  // Check if already registered (match by script path, not scope, to avoid
  // colliding with the Vite PWA service worker which also owns scope '/').
  const existing = await navigator.serviceWorker.getRegistrations();
  const found = existing.find(
    (r) =>
      r.active?.scriptURL.includes(SW_PATH) ||
      r.installing?.scriptURL.includes(SW_PATH) ||
      r.waiting?.scriptURL.includes(SW_PATH),
  );
  if (found) return found;

  // Use a dedicated scope so this SW does not compete with the Vite PWA SW.
  // Firebase only needs scope '/firebase-cloud-messaging-push-scope' per spec.
  return navigator.serviceWorker.register(swUrl, {
    scope: '/firebase-cloud-messaging-push-scope',
  });
}

export async function requestFcmToken(): Promise<string | null> {
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window))
      return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messaging: Messaging | null = getFirebaseMessaging();
    if (!messaging) return null;

    const swRegistration = await getOrRegisterServiceWorker();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    return token ?? null;
  } catch {
    return null;
  }
}

export async function registerFcmToken(token: string): Promise<void> {
  try {
    await apiClient.post('/fcm-tokens', { token });
    localStorage.setItem(FCM_TOKEN_KEY, token);
  } catch {
    // non-critical — will retry on next login
  }
}

export async function unregisterFcmToken(): Promise<void> {
  const token = localStorage.getItem(FCM_TOKEN_KEY);
  if (!token) return;
  try {
    await apiClient.delete('/fcm-tokens', { data: { token } });
    localStorage.removeItem(FCM_TOKEN_KEY);
  } catch {
    // non-critical
  }
}

export function onForegroundMessage(
  callback: (payload: {
    title: string;
    body: string;
    data?: Record<string, string>;
  }) => void,
): (() => void) | undefined {
  const messaging = getFirebaseMessaging();
  if (!messaging) return undefined;

  const unsub = onMessage(messaging, (payload) => {
    const title =
      payload.notification?.title ?? payload.data?.title ?? 'Dentacares';
    const body = payload.notification?.body ?? payload.data?.body ?? '';
    callback({
      title,
      body,
      data: payload.data as Record<string, string> | undefined,
    });
  });

  return unsub;
}
